import { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import { Link } from "react-router-dom";

import { Box } from "components/common";
import TimezoneDropdown from "components/common/TimezoneDropdown";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import TimezoneDescription from "components/settings/TimezoneDescription";
import {
    UPDATE_TIMEZONE_FAIL_MESSAGE,
    UPDATE_TIMEZONE_SUCCESS_MESSAGE,
} from "constant";
import { useAppContext } from "context/appContext";
import OrganisationService from "service/organisationService";
import {
    changeOrganizationAction,
    storeOrganizationAction,
    storeUserAction,
} from "store/actions";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { canAccess } from "utils/authorize-action";
import { isHttpSuccess } from "utils/functions";

const DefaultTZ = "UTC";

const TimezoneSettings = () => {
    const {
        storeData: {
            user: { userData },
            organization: {
                currentOrgInfo: { uuid: orgId, timeZone, isDefault },
                orgList,
            },
        },
        storeDispatchActions,
    } = useAppContext();

    const [originTimezone, setOriginTimezone] = useState("");
    const [timezone, setTimezone] = useState("");
    const [hasPermission, setHasPermission] = useState(false);

    useEffect(() => {
        const tz = timeZone ?? DefaultTZ;
        setOriginTimezone(tz);
        setTimezone(tz);
    }, [timeZone]);

    useEffect(() => {
        setHasPermission(canAccess("organisation:update"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.authorizationConfig]);

    const canSave = () => originTimezone !== timezone;

    const handleTimezoneChange = (tz: string) => {
        setTimezone(tz);
    };

    const updateTimeZone = async () => {
        const res = await OrganisationService.updateOrganisation(orgId, {
            time_zone: timezone,
        });

        if (!isHttpSuccess(res.status)) {
            showErrorAlert(getAPIError(res, UPDATE_TIMEZONE_FAIL_MESSAGE));
            return;
        }

        showSuccessAlert({
            message: UPDATE_TIMEZONE_SUCCESS_MESSAGE,
        });

        setOriginTimezone(timezone);
        orgList.forEach((org) => {
            if (org.uuid === orgId) {
                org.timeZone = res.data.time_zone;
            }
        });
        storeDispatchActions(storeOrganizationAction({ orgList }));
        storeDispatchActions(
            changeOrganizationAction({
                orgInfo: res.data,
            })
        );

        if (isDefault) {
            storeDispatchActions(
                storeUserAction({
                    userData: {
                        ...userData,
                        timezone,
                    },
                    isLoaded: true,
                })
            );
        }
    };

    return (
        <ContentWrapper title="Time Zone Settings">
            <div className="page-content gateway-list-page">
                <Container>
                    <Row>
                        <Col sm={12}>
                            <h5 className="page-title">Time Zone Settings</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <Breadcrumb className="w-100">
                                <Breadcrumb.Item linkAs="span">
                                    <Link to="/settings">Settings</Link>{" "}
                                </Breadcrumb.Item>
                                <Breadcrumb.Item active>
                                    Time Zone Settings
                                </Breadcrumb.Item>
                            </Breadcrumb>
                        </Col>
                    </Row>
                    <Box>
                        <Row className="d-flex flex-column align-items-start flex-sm-row align-items-sm-center px-2 px-xl-4">
                            <span className="mr-3 mr-xl-5 flex-shrink-1">
                                Time zone
                            </span>
                            <div className="d-flex flex-column justify-content-between flex-sm-row flex-fill">
                                <TimezoneDropdown
                                    value={timezone}
                                    onChange={handleTimezoneChange}
                                    className="dark mr-2 flex-grow-1"
                                    disabled={!hasPermission}
                                />
                                <div className="mt-2 mt-sm-auto ">
                                    <Button
                                        className="px-xl-4"
                                        disabled={!canSave()}
                                        onClick={updateTimeZone}
                                    >
                                        SAVE
                                    </Button>
                                </div>
                            </div>
                        </Row>
                    </Box>
                    <div className=" mt-3">
                        <TimezoneDescription />
                    </div>
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default TimezoneSettings;
